'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'

import { cx } from '@/lib/utils'

interface HearingAidsTabsProps {
  /** `tag` is the panel key; `label` is what the button shows. */
  tabs: { tag: string; label: string }[]
  /** Pre-rendered panel for each tab, keyed by tag name */
  panels: { tag: string; content: ReactNode }[]
}

export const HearingAidsTabs = ({ tabs, panels }: HearingAidsTabsProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [selectedTag, setSelectedTag] = useState(tabs[0]?.tag ?? '')
  const [mountRemainingPanels, setMountRemainingPanels] = useState(false)

  useEffect(() => {
    const section = sectionRef.current
    if (!section || typeof IntersectionObserver === 'undefined') {
      setMountRemainingPanels(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setMountRemainingPanels(true)
          observer.disconnect()
        }
      },
      { rootMargin: '300px 0px' }
    )
    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={sectionRef} className="w-full min-w-0">
      <div
        className={cx(
          'mb-14 flex w-[calc(100%+48px)] items-start gap-[10px] overflow-x-auto overflow-y-hidden [scrollbar-width:none]',
          'max-xs:w-[calc(100%+20px)]',
          'desktop-xl:w-[calc(100%+96px)] desktop-xl:gap-[16px]'
        )}
      >
        {tabs.map((tab) => (
          <button
            key={tab.tag}
            type="button"
            data-tracking="ignore"
            onClick={() => {
              setSelectedTag(tab.tag)
              setMountRemainingPanels(true)
            }}
            aria-pressed={selectedTag === tab.tag}
            className={cx(
              'flex max-h-[42px] min-w-fit cursor-pointer items-center justify-center gap-[8px] rounded-[60px] border p-[10px_18px] text-[16px] leading-[140%] font-normal tracking-[-0.48px]',
              'max-xs:max-h-[36px] max-xs:gap-[10px] max-xs:p-[8px_16px] max-xs:tracking-[-0.42px]',
              'desktop-xl:max-h-[53px] desktop-xl:gap-[16px] desktop-xl:p-[16px_28px] desktop-xl:text-[20.8px] desktop-xl:leading-[100%] desktop-xl:tracking-[-0.624px]',
              selectedTag === tab.tag ? 'bg-lapis border-0 text-white' : 'border-navy/20'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {panels.map((panel, index) =>
        index === 0 || mountRemainingPanels ? (
          <div key={panel.tag} hidden={panel.tag !== selectedTag} className="min-w-0 w-full">
            {panel.content}
          </div>
        ) : null
      )}
    </div>
  )
}
